/**
 * 택배사 코드/이름/배송조회 URL 매핑
 *
 * 자체배송(self_delivery) 캠페인에서 운영자가 선정자별 송장번호를 입력하면,
 * 참여자가 이 매핑을 이용해 택배사 배송조회 페이지로 이동할 수 있어요.
 * cafe24_order 캠페인은 기존 Cafe24 주문 배송조회를 쓰므로 대상이 아니에요.
 */

// 택배사 정의 — 코드/표시명/조회 URL 생성 함수
export interface Courier {
  code: string
  label: string
  // 송장번호를 받아 배송조회 URL을 반환. 조회 URL이 없으면(기타 등) null 반환
  trackingUrl: (invoice: string) => string | null
}

// 송장번호를 URL에 넣기 전 정리 — 하이픈/공백 제거 후 URL 인코딩
function cleanInvoice(invoice: string): string {
  return encodeURIComponent(invoice.replace(/[\s-]/g, ''))
}

// URL 템플릿의 {INVOICE} 자리를 정리된 송장번호로 치환하는 헬퍼
function urlBuilder(template: string): (invoice: string) => string | null {
  return (invoice: string) => {
    const cleaned = cleanInvoice(invoice)
    if (!cleaned) return null
    return template.replace('{INVOICE}', cleaned)
  }
}

// 지원 택배사 목록 (Select 드롭다운 노출 순서와 동일)
export const COURIERS: Courier[] = [
  {
    code: 'cj',
    label: 'CJ대한통운',
    trackingUrl: urlBuilder(
      'https://trace.cjlogistics.com/next/tracking.html?wblNo={INVOICE}',
    ),
  },
  {
    code: 'epost',
    label: '우체국택배',
    trackingUrl: urlBuilder(
      'https://service.epost.go.kr/trace.RetrieveDomRigiTraceList.comm?sid1={INVOICE}&displayHeader=N',
    ),
  },
  {
    code: 'hanjin',
    label: '한진택배',
    trackingUrl: urlBuilder(
      'https://www.hanjin.com/kor/CMS/DeliveryMgr/WaybillResult.do?mCode=MN038&schLang=KR&wblnumText2={INVOICE}',
    ),
  },
  {
    code: 'lotte',
    label: '롯데택배',
    trackingUrl: urlBuilder(
      'https://www.lotteglogis.com/open/tracking?InvNo={INVOICE}',
    ),
  },
  {
    code: 'logen',
    label: '로젠택배',
    trackingUrl: urlBuilder(
      'https://www.ilogen.com/web/personal/trace/{INVOICE}',
    ),
  },
  {
    code: 'gspostbox',
    label: 'GS Postbox(반값택배)',
    trackingUrl: urlBuilder(
      'https://www.cvsnet.co.kr/invoice/tracking.do?invoice_no={INVOICE}',
    ),
  },
  {
    code: 'cupost',
    label: 'CU편의점택배',
    trackingUrl: urlBuilder(
      'https://www.cupost.co.kr/postbox/delivery/localResult.cupost?invoice_no={INVOICE}',
    ),
  },
  {
    code: 'daesin',
    label: '대신택배',
    trackingUrl: urlBuilder(
      'https://www.ds3211.co.kr/freight/internalFreightSearch.ht?billno={INVOICE}',
    ),
  },
  {
    code: 'kdexp',
    label: '경동택배',
    trackingUrl: urlBuilder(
      'https://kdexp.com/newDeliverySearch.kd?barcode={INVOICE}',
    ),
  },
  {
    // 목록에 없는 택배사 — 참여자가 직접 조회하도록 안내 (조회 URL 없음)
    code: 'etc',
    label: '기타(직접 조회)',
    trackingUrl: () => null,
  },
]

// 코드 → 택배사 조회 맵 (헬퍼 내부용)
const COURIER_MAP: Record<string, Courier> = Object.fromEntries(
  COURIERS.map((c) => [c.code, c]),
)

// 코드로 택배사 정보 조회 (없으면 undefined)
export function getCourier(
  code: string | null | undefined,
): Courier | undefined {
  if (!code) return undefined
  return COURIER_MAP[code]
}

// 코드로 택배사 표시명 조회 (등록되지 않은 코드는 코드 문자열 그대로 반환)
export function getCourierLabel(code: string | null | undefined): string {
  return getCourier(code)?.label ?? code ?? ''
}

// 택배사 코드 + 송장번호로 배송조회 URL 생성 (조회 불가 시 null)
export function getTrackingUrl(
  code: string | null | undefined,
  invoice: string | null | undefined,
): string | null {
  if (!code || !invoice) return null
  const courier = getCourier(code)
  if (!courier) return null
  return courier.trackingUrl(invoice)
}
